import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { KeenIcon } from '@/components';
import { Can } from '@casl/react';
import { useAbility } from '@/providers/';
import { useAuthContext } from '@/auth';
import { useNavigate } from 'react-router-dom';
import { Container } from '@/components/container';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import DataTable from 'datatables.net-react';
import DT from 'datatables.net-dt';
import 'datatables.net-select-dt';
import 'datatables.net-responsive-dt';
import { createRoot } from 'react-dom/client';
import { createDataTableOptions, createDataTableConfig, getAccessToken } from '@/utils';
import { ReminderModal, renderAssignees, TaskTitle } from '@/components/reminders';
import { ConfirmationModal } from '@/components/modal';
import { useConfirmationModal, useMatterOptions, useUserOptions } from '@/hooks';
import { SelectInput, DateInput, FilterContainer, ColumnToggleDropdown } from '@/components/TableFilter';
import reminderService from '@/services/reminder/reminderService';

DataTable.use(DT);

const STATUS_OPTIONS = [
  { label: 'Pending', value: 'pending' },
  { label: 'Completed', value: 'completed' },
];

const SCOPE_OPTIONS = [
  { label: 'My Tasks', value: 'mine' },
  { label: 'All Tasks', value: 'all' },
];

/**
 * The quick filter chips (OL-210). Overdue, Today and Next 7 days only ever
 * show open tasks; the server's `due` scopes see to that.
 */
const QUICK_FILTERS = [
  { key: 'open', label: 'Open', status: STATUS_OPTIONS[0], due: null },
  { key: 'overdue', label: 'Overdue', status: null, due: 'overdue' },
  { key: 'today', label: 'Today', status: null, due: 'today' },
  { key: 'week', label: 'Next 7 days', status: null, due: 'week' },
  { key: 'all', label: 'All', status: null, due: null },
];

const DEFAULT_FILTERS = {
  mine: 1,
  status: STATUS_OPTIONS[0],
  due: null,
  matter_id: null,
  assignee: null,
  due_at_from: null,
  due_at_to: null,
  date_range: null,
};

const activeQuickFilter = ({ due, status }) => {
  if (due) return due;
  if (!status) return 'all';
  return status.value === 'pending' ? 'open' : null;
};

/**
 * Standalone tasks list (labelled "Tasks", still /reminders) — the one place
 * that shows completed tasks and ones delegated to other users, which the
 * dashboard panel and the per-record tabs both hide. Defaults to the current
 * user's own open tasks.
 */
const Reminders = () => {
  const { logout } = useAuthContext();
  const { ability } = useAbility();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('create');
  const [selectedData, setSelectedData] = useState(null);
  const [itemId, setItemId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const { isOpen, actionId, openModal, closeModal } = useConfirmationModal();

  const canViewAll = ability.can('view', 'all-reminder');
  const loadMatterOptions = useMatterOptions({ defaultPageSize: 20 });
  const loadUserOptions = useUserOptions({ defaultPageSize: 20 });

  const [filterData, setFilterData] = useState(DEFAULT_FILTERS);

  const token = getAccessToken();
  const tableRef = useRef(null);
  const filterRef = useRef(filterData);
  const formikRef = useRef();

  useEffect(() => {
    filterRef.current = filterData;
  }, [filterData]);

  const columns = [
    {
      title: 'Title',
      name: 'title',
      data: 'title',
      render: function (value, type, row) {
        return type === 'display' ? `<div id="reminder-title-${row.id}"></div>` : value;
      },
    },
    { title: 'Due', name: 'due_at', data: 'due_at' },
    {
      title: 'Status',
      name: 'status',
      data: 'status',
      render: function (value, type, row) {
        const badgeClass = value === 'completed'
          ? 'badge-success'
          : row.is_overdue ? 'badge-danger' : 'badge-warning';
        const label = value === 'pending' && row.is_overdue ? 'overdue' : value;
        return `<span class="badge badge-pill badge-outline capitalize ${badgeClass}">${label}</span>`;
      },
    },
    {
      title: 'Related To',
      name: 'remindable',
      data: null,
      orderable: false,
      render: function (data, type, row) {
        return `<div id="reminder-parent-${row.id}"></div>`;
      },
    },
    {
      title: 'Assigned To',
      name: 'assigned_to',
      data: null,
      searchable: false,
      render: function (data, type, row) {
        return `<div id="reminder-assigned-${row.id}"></div>`;
      },
    },
    {
      title: 'Notes',
      name: 'notes',
      data: 'notes',
      render: function (value) {
        if (!value) return '-';
        return value.length > 60 ? `${value.slice(0, 60)}…` : value;
      },
    },
    { title: 'Created At', name: 'created_at', data: 'created_at' },
    {
      title: 'Actions',
      name: 'actions',
      data: null,
      orderable: false,
      searchable: false,
      render: function (data, type, row) {
        return `<div id="reminder-action-${row.id}"></div>`;
      },
    },
  ];

  const defaultVisibleColumns = ['title', 'due_at', 'status', 'remindable', 'assigned_to', 'actions'];

  const refresh = () => {
    tableRef.current?.dt()?.ajax.reload();
  };

  const handleSubmit = async (formData) => {
    const isEdit = mode === 'edit';
    const action = isEdit ? reminderService.updateReminder : reminderService.createReminder;
    const args = isEdit ? [formData, itemId] : [formData];
    try {
      isEdit ? setLoading(true) : setIsLoading(true);
      const { status } = await action(...args);
      if (status) {
        handleModalClose();
        refresh();
        toast.success(`Task ${isEdit ? 'Updated' : 'Created'} Successfully`, {
          description: `Task ${isEdit ? 'updated' : 'created'} successfully.`,
        });
      }
    } catch (error) {
      console.error('Error', error);
      if (error.message == '422 Unprocessable Entity. Validation failed.') {
        const formattedErrors = Object.fromEntries(
          Object.entries(error.errors).map(([key, value]) => [key, value[0]])
        );
        formikRef.current?.setErrors(formattedErrors);
        toast.error('Validation Error.', {
          description: 'Please check your input. Some fields may be invalid.',
        });
        return;
      }
      toast.error('Oops! Something went wrong.', {
        description: 'An error occurred while saving the task. Please try again in a moment.',
      });
    } finally {
      isEdit ? setLoading(false) : setIsLoading(false);
    }
  };

  const handleEdit = async (id) => {
    setItemId(id);
    setMode('edit');
    setOpen(true);
    try {
      const { data, status } = await reminderService.getSingleReminder(id);
      if (status) {
        setSelectedData(data);
      }
    } catch (error) {
      console.error('Error', error);
      toast.error('Failed to fetch Task.', {
        description: 'An error occurred while fetching the task. Please try again later.',
      });
    }
  };

  const handleToggleStatus = async (row) => {
    const isCompleted = row.status === 'completed';
    try {
      const { status } = isCompleted
        ? await reminderService.reopenReminder(row.id)
        : await reminderService.completeReminder(row.id);
      if (status) {
        refresh();
        toast.success(`Task ${isCompleted ? 'Reopened' : 'Completed'}`, {
          description: `Task ${isCompleted ? 'reopened' : 'marked complete'} successfully.`,
        });
      }
    } catch (error) {
      console.error('Error', error);
      toast.error('Failed to update Task.', {
        description: 'An error occurred while updating the task. Please try again later.',
      });
    }
  };

  const handleDelete = async () => {
    closeModal();
    try {
      const { status } = await reminderService.deleteReminder(actionId);
      if (status) {
        refresh();
        toast.success('Deleted Task.', {
          description: 'Task deleted Successfully.',
        });
      }
    } catch (error) {
      console.error('Error', error);
      toast.error('Failed to delete Task.', {
        description: 'An error occurred while deleting the task. Please try again later.',
      });
    }
  };

  const handleModalClose = () => {
    setItemId(null);
    setSelectedData(null);
    setMode('create');
    setOpen(false);
  };

  const renderParent = (row) => {
    const { remindable, remindable_alias } = row;
    if (!remindable || !remindable_alias) {
      return <span className="text-gray-500">General</span>;
    }

    const label = remindable_alias === 'contact'
      ? `${remindable.first_name || ''} ${remindable.last_name || ''}`.trim() || 'Contact'
      : remindable.name || 'Matter';

    return (
      <span
        onClick={() => navigate(remindable_alias === 'contact'
          ? `/contacts/${remindable.id}?tab=Reminders`
          : `/matters/detail/${remindable.id}?tab=Reminders`)}
        className="cursor-pointer link"
      >
        {label}
      </span>
    );
  };

  const renderCustomActions = (row) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="text-gray-600">
          <KeenIcon icon="dots-vertical" className="text-[18px]" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <Can I="handle" a="reminder" ability={ability}>
          <DropdownMenuItem onClick={() => handleEdit(row.id)}>
            <KeenIcon icon="notepad-edit" className="mr-0.5 h-4 w-4 cursor-pointer" /> Edit
          </DropdownMenuItem>
        </Can>
        <Can I="handle" a="reminder" ability={ability}>
          <DropdownMenuItem onClick={() => handleToggleStatus(row)}>
            <KeenIcon icon="check-circle" className="mr-0.5 h-4 w-4 cursor-pointer" />
            {row.status === 'completed' ? 'Reopen' : 'Mark Complete'}
          </DropdownMenuItem>
        </Can>
        <Can I="delete" a="reminder" ability={ability}>
          <DropdownMenuItem onClick={() => openModal(row.id)} className="text-red-600 cusror-pointer">
            <KeenIcon icon="trash" className="mr-0.5 h-4 w-4" /> Delete
          </DropdownMenuItem>
        </Can>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const ajaxConfig = createDataTableConfig('/reminders/data_table_list', filterRef, token, logout);

  const options = createDataTableOptions({
    order: [[1, 'asc']],
    initComplete: function () {
      const api = this.api();
      api.columns().every(function () {
        const col = this;
        const name = col.settings()[0].aoColumns[col.index()].name;
        if (name && !defaultVisibleColumns.includes(name)) {
          col.visible(false);
        }
      });
    },
    drawCallback: function () {
      const api = this.api();
      api.rows({ page: 'current' }).data().each((row) => {
        const mount = (id, node) => {
          const container = document.getElementById(id);
          if (!container) return;
          if (!container._reactRoot) {
            container._reactRoot = createRoot(container);
          }
          container._reactRoot.render(node);
        };

        mount(`reminder-action-${row.id}`, renderCustomActions(row));
        mount(`reminder-assigned-${row.id}`, renderAssignees(row.assigned_to));
        mount(`reminder-parent-${row.id}`, renderParent(row));
        mount(`reminder-title-${row.id}`, <TaskTitle reminder={row} />);
      });
    },
  });

  const handleFilterChange = (key, val) => {
    setFilterData((prev) => {
      const updated = { ...prev, [key]: val };
      filterRef.current = updated;
      return updated;
    });
  };

  const resetFilters = () => {
    setFilterData(DEFAULT_FILTERS);
    filterRef.current = DEFAULT_FILTERS;
    refresh();
  };

  const applyQuickFilter = ({ status, due }) => {
    const updated = { ...filterRef.current, status, due };
    filterRef.current = updated;
    setFilterData(updated);
    refresh();
  };

  const quickFilter = activeQuickFilter(filterData);

  return (
    <Container>
      <FilterContainer show={showFilter} onApply={refresh} onReset={resetFilters}>
        <SelectInput
          label="Status"
          value={filterData.status}
          onChange={(val) => handleFilterChange('status', val)}
          staticOptions={STATUS_OPTIONS}
          customClass="sm:w-[32%] w-full"
        />

        <SelectInput
          label="Matter"
          type="matter"
          value={filterData.matter_id}
          onChange={(val) => handleFilterChange('matter_id', val)}
          loadOptions={loadMatterOptions}
          pageSize={20}
          customClass="sm:w-[32%] w-full"
        />

        <SelectInput
          label="Assigned To"
          type="users"
          value={filterData.assignee}
          onChange={(val) => handleFilterChange('assignee', val)}
          loadOptions={loadUserOptions}
          pageSize={20}
          customClass="sm:w-[32%] w-full"
        />

        {canViewAll && (
          <SelectInput
            label="Scope"
            value={filterData.mine ? 'mine' : 'all'}
            onChange={(val) => handleFilterChange('mine', val === 'mine' ? 1 : null)}
            staticOptions={SCOPE_OPTIONS}
            customClass="sm:w-[32%] w-full"
          />
        )}

        <DateInput
          customClass="sm:w-[32%] w-full"
          label="Due Date - (From, To)"
          mode="range"
          value={filterData.date_range}
          onChange={(range) => {
            setFilterData((prev) => {
              const updated = {
                ...prev,
                due_at_from: range?.from ?? null,
                due_at_to: range?.to ?? null,
                date_range: range,
              };
              filterRef.current = updated;
              return updated;
            });
          }}
        />
      </FilterContainer>

      <div className="card min-w-full mb-4">
        <div className="card-header flex-wrap gap-2">
          <div className="flex items-center flex-wrap gap-1">
            <ColumnToggleDropdown tableRef={tableRef} columns={columns} defaultVisibleColumns={defaultVisibleColumns} />
            {QUICK_FILTERS.map((option) => (
              <button
                key={option.key}
                type="button"
                className={`btn btn-sm ${quickFilter === option.key ? 'btn-primary' : 'btn-light'}`}
                onClick={() => applyQuickFilter(option)}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="flex items-center flex-wrap gap-2">
            <button className="text-xs btn btn-sm btn-primary btn-outline" onClick={() => setShowFilter((prev) => !prev)}>
              <KeenIcon icon="filter" className="w-fit btn-icon" />
              {showFilter ? 'Hide Advanced Filters' : 'Advanced Filters'}
            </button>
            <Can I="handle" a="reminder" ability={ability}>
              <button className="text-xs btn btn-sm btn-light" onClick={() => setOpen(true)}>
                <KeenIcon icon="plus" className="w-fit btn-icon" />
                Add New
              </button>
            </Can>
          </div>
        </div>

        <DataTable
          id="reminders-table"
          ajax={ajaxConfig}
          columns={columns}
          options={options}
          ref={tableRef}
        />

        <ReminderModal
          open={open}
          onClose={handleModalClose}
          mode={mode}
          data={selectedData}
          onSubmit={handleSubmit}
          createLoading={isLoading}
          updateLoading={loading}
          ref={formikRef}
        />

        <ConfirmationModal
          show={isOpen}
          onClose={closeModal}
          onConfirm={handleDelete}
          message="Are you sure you want to delete this task? This action is permanent and cannot be undone."
          title="Are You Sure?"
        />
      </div>
    </Container>
  );
};

export { Reminders };
